"use client"

import * as React from "react"
import {ChevronsUpDown, Plus} from "lucide-react"

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from "@/components/ui/sidebar"
import {MeerUserOrganizationResponseDto} from "@/api/generated";
import {Avatar, AvatarFallback, AvatarImage} from "@/components/ui/avatar";
import {useOrg} from "@/context/OrgContext";
import {useEffect} from "react";

export function OrgSwitcher({teams}: { teams: MeerUserOrganizationResponseDto[] }) {
    const {isMobile} = useSidebar()
    const orgContext = useOrg()
    
    // Find the active team based on the orgId from context
    const [activeTeam, setActiveTeam] = React.useState<MeerUserOrganizationResponseDto | null>(null)

    // Initialize active team based on orgId from context
    useEffect(() => {
        if (teams.length > 0) {
            // If orgId is set in context, find matching team
            if (orgContext.orgId) {
                const matchingTeam = teams.find(team => team.organizationId === orgContext.orgId);
                if (matchingTeam) {
                    setActiveTeam(matchingTeam);
                    return;
                }
            }
            
            // Default to first team if no match or no orgId
            setActiveTeam(teams[0]);
            // Set the orgId for the first team
            orgContext.setOrgId(teams[0].organizationId);
        }
    }, [teams, orgContext.orgId]);

    // Handle team selection
    const handleTeamChange = (team: MeerUserOrganizationResponseDto) => {
        setActiveTeam(team);
        orgContext.setOrgId(team.organizationId);
    };

    if (!activeTeam || teams.length === 0) {
        return null;
    }

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                            size="lg"
                            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                        >
                            <div
                                className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                              {/*  <activeTeam.logo className="size-4"/>*/}
                            </div>
                            <div className="grid flex-1 text-left text-sm leading-tight">
                                <span className="truncate font-medium">{activeTeam.name}</span>
                                <span className="truncate text-xs">{activeTeam.isActive}</span>
                            </div>
                            <ChevronsUpDown className="ml-auto"/>
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                        align="start"
                        side={isMobile ? "bottom" : "right"}
                        sideOffset={4}
                    >
                        <DropdownMenuLabel className="text-muted-foreground text-xs">
                            Teams
                        </DropdownMenuLabel>
                        {teams.map((team, index) => (
                            <DropdownMenuItem
                                key={team.organizationId}
                                onClick={() => handleTeamChange(team)}
                                className="gap-2 p-2"
                            >
                                <div className="flex size-6 items-center justify-center rounded-md border">
                                    <Avatar>
                                    <AvatarImage src={""}/>
                                    <AvatarFallback className="rounded-lg">{team.name?.charAt(0)}</AvatarFallback>
                                    </Avatar>

                                </div>
                                {team.name}
                                <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    )
}
